import './styles.css';
import { api, ApiError, setLibraryScope, type AiDist, type AiIo, type AiMotor, type AiPlan, type TenantInfo, type User } from './api';
import { Editor } from './editor/editor';

import { partsView } from './partsView';
import { symbolsView } from './symbolsView';
import { adminView, passwordDialog } from './adminView';
import { platformView } from './platformView';
import { landingView } from './landing';
import { aiUpload, importEplanPdf } from './pdfImport';
import { allowFile, canWriteFiles, createLocal, EXT, forgetLocal, getLocal, listLocal, openLocalFile, readLocal } from './localStore';
import { chooseLang, initLang, langPicker } from './lang';
import { registerCustomSymbols } from '@enercad/core';
import { downloadFile, esc, fmtDate, LOGO, openModal, qs, readFileText, reloadIfStale, safeFileName, toast } from './ui';
import { normaliseProject } from '@enercad/core';
import { blankProject, decodeDxf, dxfToProject, dxfUnderlayPages, MAX_INCOMER, parseSteps, t as $t, tk } from '@enercad/core';

// a tab opened before a deploy reloads once instead of failing on removed chunks
window.addEventListener('vite:preloadError', (e) => {
  if (reloadIfStale((e as Event & { payload?: unknown }).payload)) e.preventDefault();
});


const root = document.getElementById('root')!;
let me: User | null = null;
let tenant: TenantInfo | null = null;
let editor: Editor | null = null;

/* Routes: #/ (projects), #/p/<id> (editor), #/parts, #/symbols. */

/** User symbols are shared on the server; load them into the library once per page load or after edits. */
let symbolsLoaded = false;
async function loadSymbols(force = false) {
  if (symbolsLoaded && !force) return;
  try {
    registerCustomSymbols((await api.symbols()).symbols);
    symbolsLoaded = true;
  } catch {
    /* offline: projects still carry their own symbol definitions */
  }
}
async function route(): Promise<void> {
  setLibraryScope('tenant');
  editor?.destroy();
  editor = null;
  // link from the verification mail; works with or without a session
  const vm = /^#\/dogrula\/([\w-]+)/.exec(location.hash);
  if (vm) return verifyLinkView(vm[1]);
  // link from the password reset mail
  const rm = /^#\/sifre\/([\w-]+)/.exec(location.hash);
  if (rm) return resetPasswordView(rm[1]);
  if (location.hash === '#/sifremi-unuttum') return forgotPasswordView();
  let info;
  try {
    info = await api.me();
  } catch (e) {
    root.innerHTML = `<div class="shell"><div class="auth"><div class="card"><h1>${$t("Sunucuya ulaşılamadı")}</h1><p>${esc((e as Error).message)}</p><button class="btn pri" id="retry">${$t("Tekrar dene")}</button></div></div></div>`;
    qs(root, '#retry').onclick = route;
    return;
  }
  me = info.user;
  tenant = info.tenant;
  if (!me) {
    // visitors: product page at the root, sign-in and company sign-up on their own addresses
    if (info.firstRun) return authView(true, false);
    if (location.hash === '#/giris' || (location.hash === '#/kayit' && info.signup)) return authView(false, info.signup, info.canResetPassword);
    return landingView(root, info.signup);
  }
  if (location.hash === '#/giris' || location.hash === '#/kayit') {
    location.hash = '#/';
    return;
  }
  if (info.verifyPending) return verifyPendingView();
  await loadSymbols();
  if (location.hash.startsWith('#/parts')) return partsView(root, me, topbar(), bindLogout);
  if (location.hash.startsWith('#/admin')) return adminView(root, me, topbar(), bindLogout);
  if (location.hash.startsWith('#/platform')) return platformView(root, me, topbar(), bindLogout);
  if (location.hash.startsWith('#/symbols')) return symbolsView(root, me, topbar(), bindLogout, loadSymbols);
  const m = /^#\/p\/([\w-]+)/.exec(location.hash);
  if (m) return openEditor(m[1]);
  const lm = /^#\/l\/([\w-]+)/.exec(location.hash);
  if (lm) return openLocalEditor(lm[1]);
  return projectsView();
}
window.addEventListener('hashchange', route);
// language drop-downs anywhere in the app
document.addEventListener('change', (e) => {
  const el = e.target as HTMLSelectElement;
  if (el.matches?.('select[data-lang]')) void chooseLang(el.value, route);
});

/** Trial / subscription notice under the top bar. */
function planBanner(): string {
  const t = tenant;
  if (!t) return '';
  // company admins can pay right from the banner
  const pay = me?.role === 'admin' ? ` <a href="#/admin/odeme"><b>${$t("Ödeme yap")}</b></a>` : '';
  if (t.readOnly)
    return `<div class="planbar bad">${$t("{0} Projeleriniz güvende: görüntüleyebilir ve çıktı alabilirsiniz, ancak değişiklik yapılamaz. Devam etmek için bizimle iletişime geçin.", t.plan === 'trial' ? $t("Deneme süreniz doldu.") : $t("Aboneliğinizin süresi doldu."))}${pay}</div>`;
  if (t.daysLeft != null && (t.plan === 'trial' || t.daysLeft <= 14))
    return `<div class="planbar${t.daysLeft <= 3 ? ' bad' : ''}">${$t("{0} Plan: {1} · {2}{3} kullanıcı · {4}{5} proje", t.plan === 'trial' ? `${$t("Deneme sürenizin bitmesine <b>{0} gün</b> kaldı.", t.daysLeft)}` : `${$t("Aboneliğinizin bitmesine <b>{0} gün</b> kaldı.", t.daysLeft)}`, esc(t.planName), t.usage.users, t.limits.users != null ? `/${t.limits.users}` : '', t.usage.projects, t.limits.projects != null ? `/${t.limits.projects}` : '')}${pay}</div>`;
  return '';
}

function topbar() {
  return `<div class="topbar"><a class="brand" href="#/">${LOGO}EnerCAD</a><a class="btn" href="#/">${$t("Projeler")}</a><a class="btn" href="#/parts">${$t("Parçalar")}</a><a class="btn" href="#/symbols">${$t("Semboller")}</a>${me!.role === 'admin' ? `<a class="btn" href="#/admin">${$t("Firma ve kullanıcılar")}</a>` : ''}${me!.platform_admin ? `<a class="btn" href="#/platform">${$t("Platform")}</a>` : ''}
    <div class="who"><span><b>${esc(tenant?.name ?? '')}</b> · ${esc(me!.name)}${me!.role === 'admin' ? $t(" (yönetici)") : ''}</span><button class="btn" id="chpw" title="${$t("Kendi şifrenizi değiştirin")}">${$t("Şifremi değiştir")}</button><button class="btn" id="logout">${$t("Çıkış")}</button>${langPicker()}</div></div>${planBanner()}`;
}
function bindLogout() {
  root.querySelector<HTMLElement>('#chpw')?.addEventListener('click', passwordDialog);
  qs(root, '#logout').onclick = async () => {
    await api.logout().catch(() => {});
    location.hash = '#/';
    route();
  };
}

/* ---------- sign in / company sign-up / first-run setup ---------- */
function authView(firstRun: boolean, signup: boolean, canReset = false) {
  let mode: 'login' | 'register' = firstRun || location.hash === '#/kayit' ? 'register' : 'login';
  const render = () => {
    const reg = mode === 'register';
    root.innerHTML = `<div class="shell"><div class="topbar"><a class="brand" href="#/">${LOGO}EnerCAD</a><span class="spacer" style="flex:1"></span>${langPicker()}${firstRun ? '' : `<a class="btn" href="#/">${$t("Tanıtım sayfası")}</a>`}</div>
    <div class="auth"><form class="card" id="f" novalidate>
      <h1>${firstRun ? $t("Kurulum: platform yöneticisi") : reg ? $t("Firma hesabı oluştur") : $t("Oturum aç")}</h1>
      <p>${firstRun ? $t("Bu ilk hesap platformu yönetir: firmalar, planlar ve ortak kütüphane. Kendi firmanız da süresiz olarak açılır.") : reg ? $t("Firmanız için bir hesap açın; 14 gün ücretsiz deneyin. Ekip arkadaşlarınızı sonra siz eklersiniz.") : $t("Elektrik şema projelerinize erişmek için giriş yapın.")}</p>
      ${reg ? `<label class="fld"><span>${$t("Firma adı")}</span><input id="company" autocomplete="organization"></label><label class="fld"><span>${$t("Ad soyad")}</span><input id="name" autocomplete="name"></label>` : ''}
      <label class="fld"><span>${$t("E-posta")}</span><input id="email" type="email" autocomplete="username" autocapitalize="none" autocorrect="off" spellcheck="false" required></label>
      <label class="fld"><span>${$t("Şifre")}</span><input id="pw" type="password" autocomplete="${reg ? 'new-password' : 'current-password'}" required minlength="8"></label>
      ${!reg && canReset ? `<p class="note" style="margin:-4px 0 8px;text-align:right"><a href="#/sifremi-unuttum">${$t("Şifremi unuttum")}</a></p>` : ''}
      <div class="err" id="err" role="alert"></div>
      <button class="btn pri lg" type="submit" style="width:100%;justify-content:center">${reg ? $t("Hesabı oluştur") : $t("Giriş yap")}</button>
      ${!firstRun && signup ? `<p class="note" style="margin-top:14px">${reg ? $t("Hesabınız var mı?") : $t("Firmanız henüz kayıtlı değil mi?")} <a href="#" id="sw">${reg ? $t("Giriş yapın") : $t("Firma hesabı oluşturun")}</a></p>` : ''}
      ${!firstRun && !reg ? `<p class="note">${$t("Firmanız kayıtlıysa hesabınızı firma yöneticiniz açar.")}</p>` : ''}
    </form></div></div>`;
    root.querySelector('#sw')?.addEventListener('click', (e) => {
      e.preventDefault();
      location.hash = reg ? '#/giris' : '#/kayit';
    });
    qs<HTMLFormElement>(root, '#f').onsubmit = async (e) => {
      e.preventDefault();
      const email = qs<HTMLInputElement>(root, '#email').value.trim();
      const pw = qs<HTMLInputElement>(root, '#pw').value;
      const err = qs(root, '#err');
      if (reg && pw.length < 8) return void (err.textContent = $t("Şifre en az 8 karakter olmalı."));
      err.textContent = '';
      try {
        const company = root.querySelector<HTMLInputElement>('#company')?.value.trim() || '';
        const name = root.querySelector<HTMLInputElement>('#name')?.value.trim() || '';
        if (reg && company.length < 2) return void (err.textContent = $t("Firma adını girin."));
        if (firstRun) await api.setupPlatform(company, name, email, pw);
        else if (reg) await api.signup(company, name, email, pw);
        else await api.login(email, pw);
        if (location.hash === '#/kayit' || location.hash === '#/giris') location.hash = '#/';
        else route();
      } catch (x) {
        err.textContent = (x as Error).message;
      }
    };
    qs<HTMLInputElement>(root, reg ? '#company' : '#email').focus();
  };
  render();
}

/* ---------- e-mail verification ---------- */
const authCard = (body: string) =>
  `<div class="shell"><div class="topbar"><a class="brand" href="#/">${LOGO}EnerCAD</a><span class="spacer" style="flex:1"></span>${langPicker()}</div><div class="auth"><div class="card">${body}</div></div></div>`;

/** Signed in but the address is not confirmed yet: nothing else is reachable until it is. */
function verifyPendingView() {
  root.innerHTML = authCard(`<h1>${$t("E-posta adresinizi doğrulayın")}</h1>
    <p>${$t("{0} adresine bir doğrulama bağlantısı gönderdik. E-postadaki bağlantıyı açtığınızda hesabınız kullanıma hazır olur.", `<b>${esc(me!.email)}</b>`)}</p>
    <p class="note">${$t("E-posta gelmediyse istenmeyen (spam) klasörüne bakın ya da yeniden gönderin.")}</p>
    <div class="err" id="err" role="alert"></div>
    <div class="acts" style="display:flex;gap:8px;flex-wrap:wrap;margin-top:14px"><button class="btn pri" id="done">${$t("Doğruladım, devam et")}</button><button class="btn" id="again">${$t("Yeniden gönder")}</button><button class="btn" id="out">${$t("Çıkış")}</button></div>`);
  qs(root, '#done').onclick = route;
  qs(root, '#again').onclick = async () => {
    const err = qs(root, '#err');
    err.textContent = '';
    try {
      await api.resendVerify();
      toast($t("Doğrulama e-postası yeniden gönderildi"));
    } catch (x) {
      err.textContent = (x as Error).message;
    }
  };
  qs(root, '#out').onclick = async () => {
    await api.logout().catch(() => {});
    location.hash = '#/';
    route();
  };
}

async function verifyLinkView(token: string) {
  root.innerHTML = authCard(`<h1>${$t("E-posta doğrulanıyor…")}</h1>`);
  let body: string;
  try {
    await api.verifyEmail(token);
    body = `<h1>${$t("E-posta adresiniz doğrulandı")}</h1><p>${$t("Hesabınız kullanıma hazır.")}</p>`;
  } catch (x) {
    body = `<h1>${$t("Doğrulama yapılamadı")}</h1><p>${esc((x as Error).message)}</p>`;
  }
  root.innerHTML = authCard(`${body}<a class="btn pri lg" href="#/" style="width:100%;justify-content:center">${$t("Devam")}</a>`);
}

/* ---------- forgotten password ---------- */
function forgotPasswordView() {
  root.innerHTML = authCard(`<form id="f" novalidate><h1>${$t("Şifremi unuttum")}</h1>
    <p>${$t("Hesabınızın e-posta adresini yazın; şifrenizi yenilemeniz için bir bağlantı gönderelim.")}</p>
    <label class="fld"><span>${$t("E-posta")}</span><input id="email" type="email" autocomplete="username" autocapitalize="none" autocorrect="off" spellcheck="false" required></label>
    <div class="err" id="err" role="alert"></div>
    <button class="btn pri lg" type="submit" style="width:100%;justify-content:center">${$t("Bağlantı gönder")}</button>
    <p class="note" style="margin-top:14px"><a href="#/giris">${$t("Girişe dön")}</a></p></form>`);
  const email = qs<HTMLInputElement>(root, '#email');
  email.focus();
  qs<HTMLFormElement>(root, '#f').onsubmit = async (e) => {
    e.preventDefault();
    const err = qs(root, '#err');
    err.textContent = '';
    try {
      await api.forgotPassword(email.value.trim());
    } catch (x) {
      return void (err.textContent = (x as Error).message);
    }
    root.innerHTML = authCard(`<h1>${$t("E-postanızı kontrol edin")}</h1>
      <p>${$t("{0} adresine kayıtlı bir hesap varsa şifre sıfırlama bağlantısı gönderildi. Bağlantı 1 saat geçerlidir.", `<b>${esc(email.value.trim())}</b>`)}</p>
      <p class="note">${$t("E-posta gelmediyse istenmeyen (spam) klasörüne bakın.")}</p>
      <a class="btn lg" href="#/giris" style="width:100%;justify-content:center;margin-top:14px">${$t("Girişe dön")}</a>`);
  };
}

function resetPasswordView(token: string) {
  root.innerHTML = authCard(`<form id="f" novalidate><h1>${$t("Yeni şifre")}</h1>
    <p>${$t("Hesabınız için yeni bir şifre belirleyin. Açık oturumlarınız kapanır.")}</p>
    <label class="fld"><span>${$t("Yeni şifre")}</span><input id="pw" type="password" autocomplete="new-password" required minlength="8"></label>
    <label class="fld"><span>${$t("Yeni şifre (tekrar)")}</span><input id="pw2" type="password" autocomplete="new-password" required minlength="8"></label>
    <div class="err" id="err" role="alert"></div>
    <button class="btn pri lg" type="submit" style="width:100%;justify-content:center">${$t("Şifreyi değiştir")}</button></form>`);
  qs<HTMLInputElement>(root, '#pw').focus();
  qs<HTMLFormElement>(root, '#f').onsubmit = async (e) => {
    e.preventDefault();
    const pw = qs<HTMLInputElement>(root, '#pw').value, err = qs(root, '#err');
    if (pw.length < 8) return void (err.textContent = $t("Şifre en az 8 karakter olmalı."));
    if (pw !== qs<HTMLInputElement>(root, '#pw2').value) return void (err.textContent = $t("Şifreler aynı değil."));
    err.textContent = '';
    try {
      await api.resetPassword(token, pw);
    } catch (x) {
      return void (err.textContent = (x as Error).message);
    }
    root.innerHTML = authCard(`<h1>${$t("Şifreniz değiştirildi")}</h1><p>${$t("Yeni şifrenizle giriş yapabilirsiniz.")}</p>
      <a class="btn pri lg" href="#/giris" style="width:100%;justify-content:center">${$t("Giriş yap")}</a>`);
  };
}

/* ---------- project list ---------- */
async function projectsView() {
  root.innerHTML = `<div class="shell">${topbar()}<main class="list">
    <div class="list-head"><h1>${$t("Projeler")}</h1>
      <button class="btn" id="imp">${$t("İçe aktar")}</button>
      <button class="btn" id="sample">${$t("Örnek proje")}</button>
      <button class="btn" id="aiprj" hidden title="${$t('Projenizi anlatın, yapay zeka motor listesini çıkarsın, sayfalar otomatik çizilsin')}">${$t('Yapay zeka ile proje')}</button>
      <button class="btn" id="lopen" title="${$t('Bilgisayarınızdaki bir EnerCAD proje dosyasını açın; sunucuya kaydedilmez')}">${$t('Bilgisayardan aç')}</button>
      <button class="btn" id="lnew" title="${$t('Proje bilgisayarınızdaki bir dosyada tutulur; sunucuda saklanmaz')}">${$t('+ Yerel proje')}</button>
      <button class="btn pri" id="new">${$t("+ Yeni proje")}</button></div>
    <div class="projects" id="localprj"></div>
    <div class="projects" id="projects"><div class="empty">${$t("Yükleniyor…")}</div></div>
    <input type="file" id="lfile" accept="${EXT},.json,application/json" hidden>
    <input type="file" id="file" accept=".json,application/json" hidden>
    <input type="file" id="dxffile" accept=".dxf,.dwg" hidden>
    <input type="file" id="dxfconvfile" accept=".dxf,.dwg" hidden>
    <input type="file" id="eplanfile" accept=".pdf,application/pdf" hidden>
  </main></div>`;
  bindLogout();
  const box = qs(root, '#projects');
  let templates: { id: string; name: string }[] = [];
  const load = async () => {
    try {
      const { projects } = await api.list();
      templates = projects.filter((p) => p.is_template).map((p) => ({ id: p.id, name: p.name }));
      const ROLE = { owner: '', write: `<span class="pill ok">${$t("düzenleyebilir")}</span>`, read: `<span class="pill warn">${$t("salt okunur")}</span>` };
      const card = (p: (typeof projects)[number]) => `<div class="proj"><a class="nm" href="#/p/${p.id}">${esc(p.name)}${p.is_template ? ` <span class="pill ok">${$t("şablon")}</span>` : ''}</a>
              <span class="meta">${$t("{0} sayfa · sürüm {1} · {2}{3}{4} {5}", p.pages, p.version, esc(fmtDate(p.updated_at)), p.updated_by ? ` · ${esc(p.updated_by)}` : '', p.scope !== 'mine' ? $t(" · sahibi {0}", esc(p.owner)) : '', p.scope === 'shared' ? ROLE[p.role] : '')}</span>
              <div class="acts"><a class="btn" href="#/p/${p.id}">${$t("Aç")}</a><button class="btn" data-tolocal="${p.id}" data-name="${esc(p.name)}" data-own="${p.role === 'owner' ? 1 : 0}" title="${$t('Projeyi bilgisayarınızda bir dosyaya kaydeder; sonra sunucudaki kopyasını silebilirsiniz')}">${$t('Bilgisayara taşı')}</button>${p.role === 'owner' ? `<button class="btn" data-tpl="${p.id}" data-on="${p.is_template ? 0 : 1}" title="${$t("Şablonlar yeni proje açarken başlangıç olarak seçilebilir")}">${p.is_template ? $t("Şablondan çıkar") : $t("Şablon yap")}</button>` : ''}${p.role === 'owner' ? `<button class="btn danger" data-del="${p.id}" data-name="${esc(p.name)}">${$t("Sil")}</button>` : ''}</div></div>`;
      const group = (title: string, list: typeof projects) => (list.length ? `<h2 class="grouph">${title} · ${list.length}</h2>${list.map(card).join('')}` : '');
      box.innerHTML = projects.length
        ? group('Projelerim', projects.filter((p) => p.scope === 'mine')) +
          group($t("Benimle paylaşılanlar"), projects.filter((p) => p.scope === 'shared')) +
          group($t("Diğer kullanıcıların projeleri (yönetici)"), projects.filter((p) => p.scope === 'others'))
        : `<div class="empty">${$t("Henüz proje yok. <b>Yeni proje</b> ile boş bir proje açın ya da <b>Örnek proje</b> ile hazır bir motor kumandası inceleyin.")}</div>`;
    } catch (e) {
      box.innerHTML = `<div class="empty">${esc((e as Error).message)}</div>`;
    }
  };
  const create = async (body: Parameters<typeof api.create>[0]) => {
    try {
      const r = await api.create(body);
      location.hash = `#/p/${r.id}`;
    } catch (e) {
      toast((e as Error).message);
    }
  };
  qs(root, '#new').onclick = () =>
    openModal(
      `<h2>${$t("Yeni proje")}</h2><label class="fld"><span>${$t("Proje adı")}</span><input id="pn" value="Yeni proje"></label>
      <label class="fld"><span>${$t("Başlangıç")}</span><select id="pt"><option value="blank">${$t("Boş proje")}</option>${templates.map((t) => `<option value="p:${esc(t.id)}">${$t("Şablon: {0}", esc(t.name))}</option>`).join('')}</select></label>
      ${templates.length ? `<p class="note">${$t("Şablondan açılan proje, şablonun sayfalarını, ayarlarını ve kapak bilgilerini kopyalar; şablon değişmez.")}</p>` : `<p class="note">${$t("Bir projeyi listede <b>Şablon yap</b> ile işaretlerseniz burada başlangıç olarak seçebilirsiniz.")}</p>`}
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Oluştur")}</button></div>`,
      (a, dlg) => {
        if (a === 'ok') create({ template: qs<HTMLSelectElement>(dlg, '#pt').value, name: qs<HTMLInputElement>(dlg, '#pn').value });
      },
    );
  qs(root, '#sample').onclick = () => create({ template: 'sample' });

  // local projects: on this computer, in the file the user chose (or in this browser where files cannot be written)
  const localBox = qs(root, '#localprj');
  const loadLocal = async () => {
    let list: Awaited<ReturnType<typeof listLocal>> = [];
    try {
      list = await listLocal();
    } catch {
      /* no browser storage (private window): no local projects to list */
    }
    localBox.innerHTML = list.length
      ? `<h2 class="grouph">${$t('Bilgisayarımdaki projeler')} · ${list.length}</h2>
        <p class="note" style="margin:-4px 0 8px">${$t('Bu projeler bilgisayarınızda saklanır, sunucuya kaydedilmez. PDF ya da Excel alınırken proje yalnızca çıktı için gönderilir ve saklanmaz.')}</p>` +
        list.map((r) => `<div class="proj"><a class="nm" href="#/l/${r.id}" data-lopen="${r.id}">${esc(r.name)} <span class="pill ok">${$t('yerel')}</span></a>
          <span class="meta">${$t('{0} sayfa · {1} · {2}', r.data.pages.length, esc(fmtDate(r.updated)), r.file ? esc(r.file) : $t('bu tarayıcıda'))}</span>
          <div class="acts"><a class="btn" href="#/l/${r.id}" data-lopen="${r.id}">${$t("Aç")}</a><button class="btn" data-ldl="${r.id}" title="${$t('Projeyi dosya olarak indirin')}">${$t('İndir')}</button><button class="btn" data-lup="${r.id}" title="${$t('Projenin bir kopyasını sunucuya yükler: ekip çalışması, paylaşım ve revizyonlar için')}">${$t('Sunucuya yükle')}</button><button class="btn" data-lforget="${r.id}" title="${$t('Listeden kaldırır; bilgisayarınızdaki dosya silinmez')}">${$t('Listeden kaldır')}</button></div></div>`).join('')
      : '';
  };
  void loadLocal();
  localBox.addEventListener('click', async (e) => {
    const t = e.target as HTMLElement;
    const open = t.closest<HTMLElement>('[data-lopen]')?.dataset.lopen;
    if (open) {
      e.preventDefault();
      // a click: the browser may ask once more for the file, so that saving goes on into it
      const r = await getLocal(open);
      if (r) await allowFile(r).catch(() => false);
      location.hash = `#/l/${open}`;
      return;
    }
    const dl = t.closest<HTMLElement>('[data-ldl]')?.dataset.ldl;
    if (dl) {
      const r = await getLocal(dl);
      if (r) downloadFile(`${safeFileName(r.name)}${EXT}`, JSON.stringify(await readLocal(r), null, 1), 'application/json');
      return;
    }
    const up = t.closest<HTMLElement>('[data-lup]')?.dataset.lup;
    if (up) {
      const r = await getLocal(up);
      if (!r) return;
      try {
        await api.create({ data: await readLocal(r) });
        toast($t('Projenin bir kopyası sunucuya yüklendi; bilgisayarınızdaki dosya da duruyor.'));
        load();
      } catch (x) {
        toast((x as Error).message);
      }
      return;
    }
    const forget = t.closest<HTMLElement>('[data-lforget]')?.dataset.lforget;
    if (forget) {
      await forgetLocal(forget);
      toast($t('Listeden kaldırıldı; dosyanız yerinde duruyor.'));
      void loadLocal();
    }
  });
  const lfile = qs<HTMLInputElement>(root, '#lfile');
  const openRecord = (r: Awaited<ReturnType<typeof openLocalFile>>) => {
    if (r) location.hash = `#/l/${r.id}`;
  };
  qs(root, '#lopen').onclick = async () => {
    if (!canWriteFiles()) return lfile.click();
    try {
      openRecord(await openLocalFile());
    } catch (x) {
      toast(x instanceof SyntaxError ? $t("Dosya geçerli bir JSON değil.") : (x as Error).message);
    }
  };
  lfile.onchange = async () => {
    const f = lfile.files?.[0];
    lfile.value = '';
    if (!f) return;
    try {
      openRecord(await openLocalFile(f));
    } catch (x) {
      toast(x instanceof SyntaxError ? $t("Dosya geçerli bir JSON değil.") : (x as Error).message);
    }
  };
  qs(root, '#lnew').onclick = () => {
    const close = openModal(
      `<h2>${$t('Yeni yerel proje')}</h2>
      <p>${canWriteFiles()
        ? $t('Proje, şimdi seçeceğiniz dosyada bilgisayarınızda tutulur ve her değişiklikte oraya kaydedilir. Sunucuda saklanmaz.')
        : $t('Bu tarayıcı dosyaya doğrudan yazamıyor: proje bu tarayıcının deposunda tutulur, listeden dosya olarak indirebilirsiniz. Dosyaya doğrudan kaydetmek için Chrome ya da Edge kullanın.')}</p>
      <label class="fld"><span>${$t("Proje adı")}</span><input id="pn" value="${esc($t('Yeni proje'))}"></label>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Oluştur")}</button></div>`,
      async (a, dlg) => {
        if (a !== 'ok') return;
        try {
          const name = qs<HTMLInputElement>(dlg, '#pn').value.trim() || $t('Yeni proje');
          const r = await createLocal(name, blankProject(name));
          if (!r) return; // the file dialog was cancelled: the form stays
          close();
          openRecord(r);
        } catch (x) {
          toast((x as Error).message);
        }
      },
    );
  };
  let ai: { available: boolean; used?: number; limit?: number } = { available: false };
  void api.aiStatus().then((s) => {
    ai = s;
    const b = root.querySelector<HTMLButtonElement>('#aiprj');
    if (b && s.available) {
      b.hidden = false;
      b.onclick = () => aiProjectDialog(s.used ?? 0, s.limit ?? 0);
    }
  }, () => {});
  const file = qs<HTMLInputElement>(root, '#file');
  qs(root, '#imp').onclick = () => {
    const off = ai.available ? '' : `<span class="note">${me?.role === 'admin' ? $t('Yapay zeka firmanız için açık değil; Firma ve kullanıcılar sayfasından firmanızın kendi yapay zeka hesabını (Claude, ChatGPT, Gemini…) bağlayabilirsiniz.') : $t('Yapay zeka firmanız için açık değil.')}</span>`;
    const close = openModal(
      `<h2>${$t('İçe aktar')}</h2>
      <div class="impopts">
        <button class="impopt" data-m="json"><b><span class="ext">JSON</span> ${$t('EnerCAD proje dosyası')}</b><span class="note">${$t("EnerCAD'den dışa aktarılmış bir proje; yeni proje olarak eklenir.")}</span></button>
        <button class="impopt" data-m="dxfconv"><b><span class="ext">DWG · DXF</span> ${$t('Şemaya çevir (deneme)')}</b><span class="note">${$t("EPLAN düzeninde çizilmiş DWG ya da DXF şema EnerCAD projesine çevrilir: her pafta bir sayfa; teller, cihazlar, kontaklar, klemensler, tel numaraları ve kesinti noktaları. Teller siyahtan ve sembollerden farklı bir renkte (ör. kırmızı) çizilmiş olmalı.")}</span></button>
        <button class="impopt" data-m="dxf"><b><span class="ext">DWG · DXF</span> ${$t('Çizim, altlık olarak')}</b><span class="note">${$t('AutoCAD ya da başka bir programda çizilmiş proje: her pafta yeni bir sayfanın arka planına yerleşir, üstüne EnerCAD sembolleriyle çizersiniz. Altlık ayrı bir katmandır; gizlenebilir, çıktıya alınmayabilir.')}</span></button>
        <button class="impopt" data-m="eplan"><b><span class="ext">PDF</span> ${$t('EPLAN çıktısı (deneme)')}</b><span class="note">${$t("EPLAN'dan alınmış vektörel şema PDF'i EnerCAD projesine çevrilir: cihazlar (EPLAN çizimleri, etiketleri ve uç numaralarıyla), kontaklar, klemensler, teller, tel numaraları, kesinti noktaları ve ürün listesindeki parça numaraları. Etiketi okunamayan cihazlar için uyarı verilir.")}</span></button>
        <button class="impopt" data-m="ai"${ai.available ? '' : ' disabled'}><b><span class="ext">PDF · PNG · JPG</span> ${$t('Yapay zeka ile')}</b><span class="note">${$t("Yapay zeka dağıtım panosu şemasını okur: ana şalter, çıkışlar, akımlar ve kablolar. Siz kontrol edip düzeltirsiniz, sayfaları EnerCAD'in pano üreticisi çizer.")}</span>${off}</button>
      </div>
      <p class="note">${$t('DXF dosyalarından sembol ve cihaz ön görünüşü Semboller ve Parçalar sayfalarından içe aktarılır.')}</p>
      <div class="acts"><button class="btn" data-m="close">${$t('Kapat')}</button></div>`,
      (a) => {
        close();
        if (a === 'json') file.click();
        else if (a === 'dxf') dxfFile.click();
        else if (a === 'dxfconv') dxfConvFile.click();
        else if (a === 'eplan') eplanFile.click();
        else if (a === 'ai' && ai.available) aiDistDialog(ai.used ?? 0, ai.limit ?? 0, true);
      },
    );
  };
  // EPLAN schematic PDF → a new project: wires, wire numbers and interruption points
  const eplanFile = qs<HTMLInputElement>(root, '#eplanfile');
  eplanFile.onchange = async () => {
    const f = eplanFile.files?.[0];
    eplanFile.value = '';
    if (!f) return;
    const close = openModal(`<h2>${$t('İçe aktarılıyor…')}</h2><p id="impst">${esc(f.name)}</p>`, () => {});
    try {
      const { pages, symbols, warnings, stats } = await importEplanPdf(f, (m) => {
        const st = document.getElementById('impst');
        if (st) st.textContent = m;
      });
      close();
      if (!pages.length) return void toast(warnings.join(' ') || $t('PDF dosyasında şema sayfası bulunamadı.'));
      const pr = blankProject(f.name.replace(/\.pdf$/i, ''));
      pr.pages = pages;
      // the EPLAN drawings of the devices travel with the project as its own symbols
      pr.symbols = symbols;
      await create({ data: pr });
      toast([$t('{0} sayfa, {1} cihaz, {2} tel içe aktarıldı', stats.pages, stats.devices, stats.wires), ...warnings].join(' · '));
    } catch (e) {
      close();
      toast((e as Error).message);
    }
  };
  // DXF drawing → a new project with one page per sheet, the drawing as underlay
  const dxfFile = qs<HTMLInputElement>(root, '#dxffile');
  // DWG is read on the server (into DXF), DXF right here
  const drawingText = async (f: File) => (/\.dwg$/i.test(f.name) ? api.dwgToDxf(f) : decodeDxf(new Uint8Array(await f.arrayBuffer())));
  // DXF schematic → a new project, converted like an EPLAN PDF
  const dxfConvFile = qs<HTMLInputElement>(root, '#dxfconvfile');
  dxfConvFile.onchange = async () => {
    const f = dxfConvFile.files?.[0];
    dxfConvFile.value = '';
    if (!f) return;
    const close = openModal(`<h2>${$t('İçe aktarılıyor…')}</h2><p>${esc(f.name)}</p>`, () => {});
    try {
      // let the dialog show before the (synchronous) conversion
      await new Promise((r) => setTimeout(r, 30));
      const { pages, symbols, warnings, stats } = dxfToProject(await drawingText(f));
      close();
      if (!pages.length) return void toast(warnings.join(' ') || $t('DXF dosyasında şema sayfası bulunamadı.'));
      const pr = blankProject(f.name.replace(/\.(dxf|dwg)$/i, ''));
      pr.pages = pages;
      pr.symbols = symbols;
      await create({ data: pr });
      toast([$t('{0} sayfa, {1} cihaz, {2} tel içe aktarıldı', stats.pages, stats.devices, stats.wires), ...warnings].join(' · '));
    } catch (e) {
      close();
      toast((e as Error).message);
    }
  };
  dxfFile.onchange = async () => {
    const f = dxfFile.files?.[0];
    dxfFile.value = '';
    if (!f) return;
    try {
      const { pages, warnings } = dxfUnderlayPages(await drawingText(f), f.name);
      if (!pages.length) return void toast(warnings.join(' ') || $t('DXF dosyasında çizim bulunamadı.'));
      const pr = blankProject(f.name.replace(/\.(dxf|dwg)$/i, ''));
      pr.pages = pages;
      if (JSON.stringify(pr).length > 19 * 1024 * 1024) return void toast($t('Çizim çok büyük; DXF dosyasını paftalara bölerek ayrı ayrı içe aktarın.'));
      await create({ data: pr });
      toast([$t('{0} sayfa içe aktarıldı', pages.length), ...warnings].join(' · '));
    } catch (e) {
      toast((e as Error).message);
    }
  };
  file.onchange = async () => {
    const f = file.files?.[0];
    if (!f) return;
    try {
      await create({ data: JSON.parse(await readFileText(f)) });
    } catch (e) {
      toast(e instanceof SyntaxError ? $t("Dosya geçerli bir JSON değil.") : (e as Error).message);
    }
    file.value = '';
  };
  box.addEventListener('click', async (e) => {
    const mv = (e.target as HTMLElement).closest<HTMLElement>('[data-tolocal]');
    if (mv) {
      const id = mv.dataset.tolocal!, name = mv.dataset.name ?? '';
      try {
        // the file is picked right on the click; the project is fetched after
        const r = await createLocal(name, async () => normaliseProject((await api.get(id)).data));
        if (!r) return;
        void loadLocal();
        if (mv.dataset.own !== '1') return void toast($t('Proje bilgisayarınıza kopyalandı.'));
        const close = openModal(
          `<h2>${$t('Bilgisayara kopyalandı')}</h2><p>${$t('"{0}" artık bilgisayarınızda. Sunucudaki kopyası silinsin mi? Silinirse revizyon geçmişi ve paylaşımları da gider; bilgisayarınızdaki dosya kalır.', esc(name))}</p>
          <div class="acts"><button class="btn" data-m="close">${$t('Sunucuda da kalsın')}</button><button class="btn danger" data-m="del">${$t('Sunucudakini sil')}</button></div>`,
          async (a) => {
            if (a !== 'del') return;
            try {
              await api.remove(id);
              close();
              toast($t('Proje artık yalnızca bilgisayarınızda.'));
              load();
            } catch (x) {
              toast((x as Error).message);
            }
          },
        );
      } catch (x) {
        toast((x as Error).message);
      }
      return;
    }
    const tp = (e.target as HTMLElement).closest<HTMLElement>('[data-tpl]');
    if (tp) {
      try {
        await api.setTemplate(tp.dataset.tpl!, tp.dataset.on === '1');
        toast(tp.dataset.on === '1' ? $t("Proje şablon olarak işaretlendi") : $t("Şablon işareti kaldırıldı"));
        load();
      } catch (x) {
        toast((x as Error).message);
      }
      return;
    }
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-del]');
    if (!b) return;
    const close = openModal(
      `<h2>${$t("\"{0}\" silinsin mi?", esc(b.dataset.name))}</h2><p>${$t("Proje listeden kaldırılır. Bu işlem arayüzden geri alınamaz.")}</p>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="del">${$t("Projeyi sil")}</button></div>`,
      async (a) => {
        if (a !== 'del') return;
        try {
          await api.remove(b.dataset.del!);
          close();
          toast($t("Proje silindi"));
          load();
        } catch (x) {
          toast((x as Error).message);
        }
      },
    );
  });
  load();
}

/* ---------- editor ---------- */
/** A local project: read from its file (or this browser's store) and edited without the server keeping it. */
async function openLocalEditor(id: string): Promise<void> {
  root.innerHTML = `<div class="shell">${topbar()}<div class="auth"><div class="empty">${$t("Proje yükleniyor…")}</div></div></div>`;
  try {
    const r = await getLocal(id);
    if (!r) throw new Error($t('Yerel proje bulunamadı.'));
    const data = await readLocal(r);
    root.innerHTML = '';
    editor = new Editor(root, { id, name: data.name, version: 0, updated_at: r.updated, role: 'owner', data, local: true });
    editor.platformAdmin = !!me?.platform_admin;
  } catch (e) {
    root.innerHTML = `<div class="shell">${topbar()}<div class="auth"><div class="card"><h1>${$t("Proje açılamadı")}</h1><p>${esc((e as Error).message)}</p><a class="btn pri" href="#/">${$t("Projelere dön")}</a></div></div></div>`;
    bindLogout();
  }
}

async function openEditor(id: string): Promise<void> {
  root.innerHTML = `<div class="shell">${topbar()}<div class="auth"><div class="empty">${$t("Proje yükleniyor…")}</div></div></div>`;
  try {
    const doc = await api.get(id);
    root.innerHTML = '';
    editor = new Editor(root, doc);
    editor.platformAdmin = !!me?.platform_admin;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return route();
    console.error(e);
    root.innerHTML = `<div class="shell">${topbar()}<div class="auth"><div class="card"><h1>${$t("Proje açılamadı")}</h1><p>${esc((e as Error).message)}</p><a class="btn pri" href="#/">${$t("Projelere dön")}</a></div></div></div>`;
    bindLogout();
  }
}

void initLang().then(route);

/* ---------- AI: describe the project, get the motor list, generate the pages ---------- */
const KIND_OPTS: [AiMotor['kind'], string][] = [['dol', tk('Direkt')], ['rev', tk('İleri-geri')], ['yd', tk('Yıldız-üçgen')], ['vfd', tk('Frekans konvertörü')], ['ss', tk('Soft starter')]];
const IO_OPTS: [AiIo['kind'], string][] = [['switch_no', tk('Şalter, kapanan (NO) · giriş')], ['switch_nc', tk('Şalter, açan (NC) · giriş')], ['lamp', tk('Sinyal lambası 24 V · çıkış')], ['valve', tk('Selenoid vana 24 V · çıkış')], ['analog', tk('Transmitter 4-20 mA · analog giriş')], ['valve_prop', tk('Oransal vana 4-20 mA · analog çıkış')]];

/** AI: describe a distribution board, check the incomer and feeder list, generate the pages. */
function aiDistDialog(used: number, limit: number, fromFile = false) {
  let plan: AiDist | null = null;
  const KINDS: [AiDist['incomer']['kind'], string][] = [['mccb', tk('Kompakt şalter (MCCB)')], ['acb', tk('Açık tip şalter (ACB)')], ['lbs', tk('Yük ayırıcı')]];
  const close = openModal(
    `<div class="gen aiprj"><h2>${$t('Yapay zeka ile dağıtım panosu')}</h2>
    <p class="note">${$t('Panoyu kendi cümlelerinizle anlatın. Yapay zeka giriş ayarlarını ve çıkış listesini çıkarır; siz kontrol edip düzeltirsiniz, sayfaları EnerCAD\'in pano üreticisi çizer.')}${limit ? ' ' + $t('Bu ay {0} / {1} istek kullanıldı.', used, limit) : ''}</p>
    <textarea id="aip" rows="5" style="width:100%;height:auto" placeholder="${$t('ör. Ofis binası ana dağıtım panosu, 400 A, jeneratör transferli, 60 kvar kompanzasyon. Çıkışlar: 3 kat aydınlatma 16 A, her katta 4 priz grubu 20 A kaçak akımlı, asansör 40 A, 2 klima panosu 63 A.')}"></textarea>
    <div class="btnrow"><button class="btn pri" data-m="ask">${$t('Çıkış listesini çıkar')}</button><span class="note" id="aist"></span></div>
    <details class="struct" style="margin-top:6px"${fromFile ? ' open' : ''}><summary>${$t('Ya da tek hat şemasını yükleyin (PDF ya da resim)')}</summary>
      <p class="note">${$t('Başka bir programda çizilmiş ya da taranmış tek hat şeması: yapay zeka ana şalteri, çıkışları, akımları, kaçak akımı ve kabloları okur. Yukarıdaki metin kutusuna yazdıklarınız ek not olarak gönderilir. En fazla 10 MB.')}</p>
      <div class="btnrow"><input type="file" id="aifile" accept="application/pdf,image/png,image/jpeg,image/webp"><button class="btn pri" data-m="file">${$t('Tek hat şemasından çıkar')}</button></div>
    </details>
    <div id="aiout"></div>
    <div class="err" id="aierr"></div>
    <div class="acts"><button class="btn" data-m="close">${$t('Vazgeç')}</button><button class="btn pri" data-m="make" disabled>${$t('Projeyi oluştur')}</button></div></div>`,
    async (a, dlg) => {
      const err = qs(dlg, '#aierr');
      err.textContent = '';
      if (a === 'ask' || a === 'file') {
        const st = qs(dlg, '#aist');
        const file = qs<HTMLInputElement>(dlg, '#aifile').files?.[0];
        if (a === 'file' && !file) return void (err.textContent = $t('Önce bir dosya seçin.'));
        if (file && a === 'file' && file.size > 10 * 1024 * 1024) return void (err.textContent = $t('Dosya çok büyük (en fazla {0} MB).', 10));
        st.textContent = a === 'file' ? $t('Yapay zeka tek hat şemasını okuyor… (bir dakika kadar sürebilir)') : $t('Yapay zeka düşünüyor…');
        dlg.querySelectorAll<HTMLButtonElement>('[data-m="ask"],[data-m="file"],[data-m="make"]').forEach((b) => (b.disabled = true));
        try {
          const note = qs<HTMLTextAreaElement>(dlg, '#aip').value.trim();
          plan = a === 'file' && file
            ? await api.aiSingleLine(await aiUpload(file, (await api.aiStatus()).pdf !== false), note)
            : await api.aiDistribution(note);
          draw(dlg);
          st.textContent = '';
        } catch (e) {
          st.textContent = '';
          err.textContent = (e as Error).message;
        } finally {
          qs<HTMLButtonElement>(dlg, '[data-m="ask"]').disabled = false;
          qs<HTMLButtonElement>(dlg, '[data-m="file"]').disabled = false;
          qs<HTMLButtonElement>(dlg, '[data-m="make"]').disabled = !plan;
        }
        return;
      }
      if (a !== 'make' || !plan) return;
      const bad = read(dlg);
      if (bad) return void (err.textContent = bad);
      const btn = qs<HTMLButtonElement>(dlg, '[data-m="make"]');
      btn.disabled = true;
      try {
        const p = await api.create({ name: qs<HTMLInputElement>(dlg, '#ainm').value.trim() || plan.name || $t('Yeni proje') });
        const g = await api.generateDistribution(p.id, plan.incomer, plan.feeders, qs<HTMLInputElement>(dlg, '#ailay').checked);
        close();
        toast($t('{0} sayfa üretildi', g.pages));
        location.hash = `#/p/${p.id}`;
      } catch (e) {
        err.textContent = (e as Error).message;
        btn.disabled = false;
      }
    },
  );
  const draw = (dlg: HTMLElement) => {
    if (!plan) return;
    const i = plan.incomer;
    qs(dlg, '#aiout').innerHTML = `<div class="row2"><label class="fld"><span>${$t('Proje adı')}</span><input id="ainm" value="${esc(plan.name)}"></label>
      <label class="fld"><span>${$t('Ana şalter')}</span><select id="aik">${KINDS.map(([k, n]) => `<option value="${k}"${i.kind === k ? ' selected' : ''}>${$t(n)}</option>`).join('')}</select></label></div>
      <div class="row2"><label class="fld"><span>${$t('Ana şalter akımı (A)')}</span><input id="aia" type="number" min="16" max="${MAX_INCOMER}" value="${i.amps}"></label>
      <label class="fld"><span>${$t('Kompanzasyon kademeleri (kvar)')}</span><input id="aic" value="${i.comp.map((k) => String(k).replace('.', ',')).join('; ')}" placeholder="${$t('ör. 5, 10, 2×20 · boş bırakılırsa kompanzasyon yok')}"></label></div>
      <label class="chkline"><input type="checkbox" id="aim"${i.meter ? ' checked' : ''}> ${$t('Akım trafoları ve enerji analizörü')}</label>
      <label class="chkline"><input type="checkbox" id="ais"${i.spd ? ' checked' : ''}> ${$t('Parafudr (aşırı gerilim koruma)')}</label>
      <label class="fld"><span>${$t('Jeneratör')}</span><select id="aig"><option value="">${$t('Jeneratör yok')}</option><option value="ats"${i.ats && !i.manual ? ' selected' : ''}>${$t('Otomatik transfer (kilitli kontaktörler)')}</option><option value="manual"${i.manual ? ' selected' : ''}>${$t('Elle transfer: I-0-II komütatör şalter')}</option></select></label>
      <label class="chkline"><input type="checkbox" id="ailay" checked> ${$t('Pano yerleşimi: cihazları montaj plakasına otomatik yerleştir (2D ve 3D görünüm)')}</label>
      <div class="tblw" style="max-height:32vh"><table class="t"><thead><tr><th>${$t('Etiket')}</th><th>${$t('Açıklama')}</th><th>A</th><th>${$t('Kutup')}</th><th>${$t('Kaçak akım')}</th><th>${$t('Kablo (m)')}</th><th title="${$t('NH sigortalı yük ayırıcı')}">NH</th><th>${$t('Sayaç')}</th><th></th></tr></thead><tbody>
      ${plan.feeders.map((f, n) => `<tr data-f="${n}"><td><input data-k="tag" value="${esc(f.tag)}" placeholder="${$t('otomatik')}" style="width:60px"></td><td><input data-k="desc" value="${esc(f.desc)}"></td>
        <td><input data-k="amps" value="${f.amps}" style="width:56px" inputmode="numeric"></td>
        <td><select data-k="poles"><option value="1"${f.poles === 1 ? ' selected' : ''}>1P</option><option value="3"${f.poles === 3 ? ' selected' : ''}>3P</option></select></td>
        <td><input type="checkbox" data-k="rcd"${f.rcd ? ' checked' : ''}></td>
        <td><input data-k="len" value="${f.cableLen ?? ''}" style="width:56px" inputmode="numeric"></td>
        <td><input type="checkbox" data-k="fuse"${f.fuse ? ' checked' : ''}></td><td><input type="checkbox" data-k="meter"${f.meter ? ' checked' : ''}></td><td><button class="btn" data-delf="${n}" title="${$t('Sil')}">×</button></td></tr>`).join('')}
      </tbody></table></div>
      ${plan.assumptions.length ? `<h3>${$t('Varsayımlar')}</h3><ul class="conn">${plan.assumptions.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>` : ''}
      ${plan.questions.length ? `<h3>${$t('Karar vermeniz gerekenler')}</h3><ul class="conn">${plan.questions.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>` : ''}
      <p class="note">${$t('Üretilen şemayı teslimden önce mutlaka kontrol edin; yapay zeka hata yapabilir.')}</p>`;
    dlg.querySelectorAll<HTMLButtonElement>('[data-delf]').forEach((b) =>
      (b.onclick = (e) => {
        e.preventDefault();
        read(dlg);
        plan!.feeders.splice(+b.dataset.delf!, 1);
        draw(dlg);
      }),
    );
  };
  /** Takes the edited values back into the plan; answers an error text when a value is out of range. */
  const read = (dlg: HTMLElement): string => {
    if (!plan) return '';
    const i = plan.incomer;
    i.kind = qs<HTMLSelectElement>(dlg, '#aik').value as AiDist['incomer']['kind'];
    i.amps = Math.round(Number(qs<HTMLInputElement>(dlg, '#aia').value));
    i.meter = qs<HTMLInputElement>(dlg, '#aim').checked;
    i.spd = qs<HTMLInputElement>(dlg, '#ais').checked;
    i.ats = !!qs<HTMLSelectElement>(dlg, '#aig').value;
    i.manual = qs<HTMLSelectElement>(dlg, '#aig').value === 'manual';
    const st = parseSteps(qs<HTMLInputElement>(dlg, '#aic').value);
    if (st.error) return st.error;
    i.comp = st.steps;
    dlg.querySelectorAll<HTMLElement>('tr[data-f]').forEach((tr) => {
      const f = plan!.feeders[+tr.dataset.f!];
      const v = (k: string) => tr.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-k="${k}"]`)!.value.trim();
      f.tag = v('tag');
      f.desc = v('desc') || f.desc;
      f.amps = Number(v('amps').replace(',', '.')) || f.amps;
      f.poles = v('poles') === '1' ? 1 : 3;
      f.rcd = !!tr.querySelector<HTMLInputElement>('[data-k="rcd"]')?.checked;
      f.fuse = !!tr.querySelector<HTMLInputElement>('[data-k="fuse"]')?.checked;
      f.meter = !!tr.querySelector<HTMLInputElement>('[data-k="meter"]')?.checked;
      const len = Number(v('len'));
      if (len > 0) f.cableLen = len;
      else delete f.cableLen;
    });
    if (!(i.amps >= 16 && i.amps <= MAX_INCOMER)) return $t('Ana şalter akımı 16–{0} A arasında olmalı.', MAX_INCOMER);
    return '';
  };
  if (fromFile) {
    // opened from the import menu: pick the file at once and read it as soon as it is chosen
    const dlg = document.querySelector<HTMLElement>('.modal:last-child .dlg')!;
    const inp = qs<HTMLInputElement>(dlg, '#aifile');
    inp.addEventListener('change', () => void (inp.files?.length && qs<HTMLButtonElement>(dlg, '[data-m="file"]').click()), { once: true });
    inp.click();
  }
}

function aiProjectDialog(used: number, limit: number) {
  let plan: AiPlan | null = null;
  const close = openModal(
    `<div class="gen aiprj"><h2>${$t('Yapay zeka ile proje')}</h2>
    <p class="note">${$t('Projeyi kendi cümlelerinizle anlatın. Yapay zeka motor listesini çıkarır; siz kontrol edip düzeltirsiniz, sayfaları EnerCAD\'in doğrulanmış devre üreticisi çizer.')}${limit ? ' ' + $t('Bu ay {0} / {1} istek kullanıldı.', used, limit) : ''}</p>
    <textarea id="aip" rows="5" style="width:100%;height:auto" placeholder="${$t('ör. Atık su terfi istasyonu: 3 dalgıç pompa 7,5 kW frekans konvertörlü, bir karıştırıcı 4 kW direkt, hepsi PLC ile, kablo boyları 50 m.')}"></textarea>
    <div class="btnrow"><button class="btn pri" data-m="ask">${$t('Motor listesini çıkar')}</button><span class="note" id="aist"></span><span class="spacer"></span><button class="btn" data-m="dist" title="${$t('Ana dağıtım ya da tali pano: giriş, çıkışlar, jeneratör, kompanzasyon')}">${$t('Dağıtım panosu / tek hat →')}</button></div>
    <div id="aiout"></div>
    <div class="err" id="aierr"></div>
    <div class="acts"><button class="btn" data-m="close">${$t('Vazgeç')}</button><button class="btn pri" data-m="make" disabled>${$t('Projeyi oluştur')}</button></div></div>`,
    async (a, dlg) => {
      const err = qs(dlg, '#aierr');
      err.textContent = '';
      if (a === 'ask') {
        const prompt = qs<HTMLTextAreaElement>(dlg, '#aip').value.trim();
        const st = qs(dlg, '#aist');
        st.textContent = $t('Yapay zeka düşünüyor…');
        dlg.querySelectorAll<HTMLButtonElement>('[data-m="ask"],[data-m="make"]').forEach((b) => (b.disabled = true));
        try {
          plan = await api.aiMotors(prompt);
          draw(dlg);
          st.textContent = '';
        } catch (e) {
          st.textContent = '';
          err.textContent = (e as Error).message;
        } finally {
          qs<HTMLButtonElement>(dlg, '[data-m="ask"]').disabled = false;
          qs<HTMLButtonElement>(dlg, '[data-m="make"]').disabled = !plan?.motors.length && !plan?.io.length;
        }
        return;
      }
      if (a === 'dist') {
        close();
        return aiDistDialog(used, limit);
      }
      if (a !== 'make' || !plan) return;
      read(dlg);
      const btn = qs<HTMLButtonElement>(dlg, '[data-m="make"]');
      btn.disabled = true;
      try {
        const p = await api.create({ name: qs<HTMLInputElement>(dlg, '#ainm').value.trim() || plan.name || $t('Yeni proje') });
        const g = await api.generateMotors(p.id, plan.motors, plan.io, plan.estop);
        close();
        toast($t('{0} sayfa üretildi', g.pages));
        location.hash = `#/p/${p.id}`;
      } catch (e) {
        err.textContent = (e as Error).message;
        btn.disabled = false;
      }
    },
  );
  /** Editable preview of the motor list, the assumptions and the open questions. */
  const draw = (dlg: HTMLElement) => {
    if (!plan) return;
    qs(dlg, '#aiout').innerHTML = `<div class="row2"><label class="fld"><span>${$t('Proje adı')}</span><input id="ainm" value="${esc(plan.name)}"></label>
      <label class="fld"><span>${$t('Acil stop butonu')}</span><input id="aiestop" type="number" min="0" max="6" value="${plan.estop}" title="${$t('0: acil stop devresi çizilmez. 1–6: güvenlik rölesi ile şablon acil stop devresi')}"></label></div>
      <div class="tblw" style="max-height:34vh"><table class="t"><thead><tr><th>${$t('Etiket')}</th><th>${$t('Açıklama')}</th><th>kW</th><th>${$t('Yol verme')}</th><th>${$t('Kumanda')}</th><th>${$t('Kablo (m)')}</th><th title="${$t('Frekans konvertörünün hızını PLC analog çıkışı belirler')}">${$t('PLC hız ref.')}</th><th title="${$t('Sargılarda PTC termistör: termistör rölesi')}">PTC</th><th></th></tr></thead><tbody>
      ${plan.motors.map((m, i) => `<tr data-i="${i}"><td><input data-k="tag" value="${esc(m.tag)}" placeholder="M${i + 1}" style="width:60px"></td><td><input data-k="desc" value="${esc(m.desc)}"></td>
        <td><input data-k="kw" value="${String(m.kw).replace('.', ',')}" style="width:56px" inputmode="decimal"></td>
        <td><select data-k="kind">${KIND_OPTS.map(([k, n]) => `<option value="${k}"${m.kind === k ? ' selected' : ''}>${$t(n)}</option>`).join('')}</select></td>
        <td><select data-k="control"><option value="plc"${m.control === 'plc' ? ' selected' : ''}>PLC</option><option value="local"${m.control === 'local' ? ' selected' : ''}>${$t('Yerel')}</option><option value="auto"${m.control === 'auto' ? ' selected' : ''}>${$t('El/Otomatik')}</option><option value="autoplc"${m.control === 'autoplc' ? ' selected' : ''}>${$t('El/Otomatik (PLC)')}</option></select></td>
        <td><input data-k="cableLen" value="${m.cableLen}" style="width:56px" inputmode="numeric"></td><td><input type="checkbox" data-k="speedRef"${m.speedRef ? ' checked' : ''}${m.kind === 'vfd' && (m.control === 'plc' || m.control === 'autoplc') ? '' : ' disabled'}></td><td><input type="checkbox" data-k="ptc"${m.ptc ? ' checked' : ''}></td><td><button class="btn" data-del="${i}" title="${$t('Sil')}">×</button></td></tr>`).join('')}
      </tbody></table></div>
      ${plan.io.length ? `<h3>${$t('PLC sinyalleri (sensör, lamba, vana)')}</h3><div class="tblw" style="max-height:26vh"><table class="t"><thead><tr><th>${$t('Etiket')}</th><th>${$t('Açıklama')}</th><th>${$t('Tür')}</th><th></th></tr></thead><tbody>
      ${plan.io.map((x, i) => `<tr data-io="${i}"><td><input data-k="tag" value="${esc(x.tag)}" placeholder="${$t('otomatik')}" style="width:80px"></td><td><input data-k="desc" value="${esc(x.desc)}"></td>
        <td><select data-k="kind">${IO_OPTS.map(([k, n]) => `<option value="${k}"${x.kind === k ? ' selected' : ''}>${$t(n)}</option>`).join('')}</select></td><td><button class="btn" data-delio="${i}" title="${$t('Sil')}">×</button></td></tr>`).join('')}
      </tbody></table></div>` : ''}
      ${plan.assumptions.length ? `<h3>${$t('Varsayımlar')}</h3><ul class="conn">${plan.assumptions.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>` : ''}
      ${plan.questions.length ? `<h3>${$t('Karar vermeniz gerekenler')}</h3><ul class="conn">${plan.questions.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>` : ''}
      <p class="note">${$t('Üretilen şemayı teslimden önce mutlaka kontrol edin; yapay zeka hata yapabilir.')}</p>`;
    dlg.querySelectorAll<HTMLButtonElement>('[data-delio]').forEach((b) =>
      (b.onclick = (e) => {
        e.preventDefault();
        read(dlg);
        plan!.io.splice(+b.dataset.delio!, 1);
        draw(dlg);
      }),
    );
    dlg.querySelectorAll<HTMLButtonElement>('[data-del]').forEach((b) =>
      (b.onclick = (e) => {
        e.preventDefault();
        read(dlg);
        plan!.motors.splice(+b.dataset.del!, 1);
        draw(dlg);
      }),
    );
  };
  const read = (dlg: HTMLElement) => {
    if (!plan) return;
    plan.estop = Math.max(0, Math.min(6, Math.round(Number(qs<HTMLInputElement>(dlg, '#aiestop').value) || 0)));
    dlg.querySelectorAll<HTMLElement>('tr[data-i]').forEach((tr) => {
      const m = plan!.motors[+tr.dataset.i!];
      const v = (k: string) => tr.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-k="${k}"]`)!.value.trim();
      m.tag = v('tag');
      m.desc = v('desc') || m.desc;
      m.kw = Number(v('kw').replace(',', '.')) || m.kw;
      m.kind = v('kind') as AiMotor['kind'];
      m.control = v('control') as AiMotor['control'];
      m.cableLen = Number(v('cableLen')) || m.cableLen;
      m.speedRef = m.kind === 'vfd' && (m.control === 'plc' || m.control === 'autoplc') && !!tr.querySelector<HTMLInputElement>('[data-k="speedRef"]')?.checked;
      m.ptc = !!tr.querySelector<HTMLInputElement>('[data-k="ptc"]')?.checked;
    });
    dlg.querySelectorAll<HTMLElement>('tr[data-io]').forEach((tr) => {
      const x = plan!.io[+tr.dataset.io!];
      const v = (k: string) => tr.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-k="${k}"]`)!.value.trim();
      x.tag = v('tag');
      x.desc = v('desc') || x.desc;
      x.kind = v('kind') as AiIo['kind'];
    });
  };
}
